import { MinusIcon } from "../../assets/icons";
import { useEffect, useRef, useState } from "react";
import { useHomeGoalStore } from "../../stores/homeGoalStore";
import { useUserProfileStore } from "../../stores/profileStore";
import { SavingsSelectedCitiesEnum } from "../../savingsSelectedCities";

const CitySelectionView = () => {
	const searchInputRef = useRef<HTMLInputElement>(null);
	const searchResultControlRef = useRef<HTMLUListElement>(null);
	const { userProfile } = useUserProfileStore();
	const { selectedCities, setSelectedCities } = useHomeGoalStore();

	const [searchQuery, setSearchQuery] = useState<string>("");
	const [showSearchResult, setShowSearchResult] = useState<boolean>(false);

	const [filteredCityKeys, setFilteredCityKeys] = useState<string[]>([]);

	useEffect(() => {
		const keys = userProfile?.homeGoal?.selectedCities ?? [];
		setSelectedCities(keys);
	}, [userProfile, setSelectedCities]);

	const handleRemoveCity = () => {
		setSelectedCities([]);
	};

	const handleSelectCity = (cityKey: string) => {
		setSelectedCities([cityKey]);
		setShowSearchResult(false);
		setSearchQuery("");
	};

	return (
		<div className="h-full px-4 pt-2 text-black w-full space-y-3">
			<div className="w-full">
				<div className="font-bold text-2xl">Where would you like to live?</div>
				<div className="text-sm">Pick the city that feels like home</div>
			</div>

			<div className="w-full relative">
				<input
					ref={searchInputRef}
					type="text"
					placeholder="Enter a city"
					className="h-16 w-full text-z-shark font-medium text-sm px-3 rounded-xl border border-[#EAEAEA] placeholder:text-[#D1D1D1]"
					value={searchQuery}
					onChange={(e) => {
						const q = e.target.value;
						setSearchQuery(q);
						setShowSearchResult(true);

						const filteredKeys = Object.entries(SavingsSelectedCitiesEnum)
							.filter(
								([key, val]) =>
									val.toLowerCase().includes(q.toLowerCase()) &&
									!selectedCities.includes(key),
							)
							.map(([key]) => key);

						setFilteredCityKeys(filteredKeys);
					}}
					disabled={selectedCities.length === 1}
				/>

				{showSearchResult && selectedCities.length === 0 && (
					<div className="absolute top-[74px] w-full">
						<ul
							ref={searchResultControlRef}
							className="bg-white min-h-12 max-h-48 overflow-y-auto border border-[#EAEAEA] rounded-xl w-full z-10"
						>
							{filteredCityKeys.length > 0 ? (
								filteredCityKeys.map((key) => (
									<li
										key={key}
										className="h-12 flex items-center border-t border-t-[#EAEAEA] first:border-t-0 px-3 cursor-pointer"
										onClick={() => handleSelectCity(key)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												handleSelectCity(key);
											}
										}}
									>
										{
											SavingsSelectedCitiesEnum[
												key as keyof typeof SavingsSelectedCitiesEnum
											]
										}
									</li>
								))
							) : (
								<li className="absolute font-semibold top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
									No cities found
								</li>
							)}
						</ul>
					</div>
				)}
			</div>

			{selectedCities.length === 1 ? (
				<div className="mt-12 space-y-3 px-4">
					<span className="text-sm font-semibold text-z-shark">
						Selected city
					</span>
					<ul className="flex flex-col gap-4">
						<li
							onClick={handleRemoveCity}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									handleRemoveCity();
								}
							}}
							className="flex items-center justify-between bg-[#FCF9EA] rounded-lg px-3 py-3 text-xl text-z-shark font-semibold cursor-pointer"
						>
							<span>
								{
									SavingsSelectedCitiesEnum[
										selectedCities[0] as keyof typeof SavingsSelectedCitiesEnum
									]
								}
							</span>
							<MinusIcon />
						</li>
					</ul>
				</div>
			) : (
				<div className="text-[#E31D1C] text-xs mt-5 px-4">
					Please select a city to proceed
				</div>
			)}
		</div>
	);
};

export { CitySelectionView };
